/** Coin, Gem, GemShard, GemShardGroup, PowerupCollectable, ReDash, RePack, ForceDash. */
import { GameBehaviour } from '../game.ts';
import type { SimCollider, SimGo } from '../world.ts';
import { FrameCounter, FrameCounterRange, lerp, moveTowardsV, type RGBA, type V2 } from '../unity.ts';
import { Jetpack, type PlayerController, type PowerupType } from '../player.ts';
import { bool, num, rgba } from '../props.ts';
import { ActivatorBase } from './activators.ts';
import { playerOf, setAlpha } from './util.ts';
import { Animator } from './animator.ts';
import type { SpriteRef } from '../../../data/gameData.ts';

/** GetComponentInChildren<SpriteRenderer>() */
export function spriteGoIn(go: SimGo): SimGo | null {
  return go.descendants().find((g) => g.sprite && g.active) ?? go.descendants().find((g) => g.sprite) ?? null;
}

function spriteRef(game: { gd: { spriteById: Map<string, SpriteRef> } }, r: any): SpriteRef | null {
  return r?.guid ? game.gd.spriteById.get(`${r.guid}:${r.fileID}`) ?? null : null;
}

/** Coin / GemShard: neighbouring pickups start their loop at different points (offset by x within 75 units) */
function playOffset(go: SimGo, state: string) {
  const range = 75;
  go.getBehaviour(Animator)?.play(state, ((go.position.x % range) / range) * -1);
}

export class Coin extends ActivatorBase {
  isConsumed = false;
  override start() {
    playOffset(this.go, 'CoinAnimation');
  }
  override onTriggerEnter(other: SimCollider) {
    if (this.isConsumed) return;
    const p = playerOf(other);
    if (p) this.onConsumed(p);
  }
  private onConsumed(p: PlayerController) {
    if (this.isConsumed) return;
    this.activate();
    this.isConsumed = true;
    if (this.go.sprite) this.go.sprite.enabled = false;
    const c = this.go.colliders[0];
    if (c) c.enabled = false;
    const pos = this.go.position;
    this.game.emit({ kind: 'sound', name: 'Coin', x: pos.x, y: pos.y });
    this.game.levelAction('coin', p, pos);
  }
}

export class Gem extends ActivatorBase {
  isConsumed = false;
  isSpawned = true;
  override onTriggerEnter(other: SimCollider) {
    if (this.isConsumed) return;
    const p = playerOf(other);
    if (p) this.onConsumed(p);
  }
  setAlpha(a: number) {
    setAlpha(this.go, a);
  }
  despawn() {
    this.isSpawned = false;
    const c = this.go.colliders[0];
    if (c) c.enabled = false;
    if (this.go.sprite) this.go.sprite.enabled = false;
  }
  private onConsumed(p: PlayerController) {
    if (this.isConsumed) return;
    this.activate();
    this.isConsumed = true;
    this.despawn();
    this.sound('gem');
    this.game.levelAction('gem', p, this.go.position);
  }
}

export const enum GemShardState {
  Hidden,
  Spawning,
  Spawned,
  Despawning,
}

export class GemShard extends ActivatorBase {
  isConsumed = false;
  spriteGo: SimGo | null = null;
  fadeOutCounter = FrameCounter.seconds(0.3);
  minAlpha = 0.2;
  bounceHeight = 0;
  spawnCounter: FrameCounter | null = null;
  spawnState = GemShardState.Spawned;
  localPosition: V2 = { x: 0, y: 0 };
  moveTowardsStarSpeed = 0;
  override noSave = ['spriteGo', 'localPosition'];

  override awake() {
    this.spriteGo = spriteGoIn(this.go);
    this.fadeOutCounter = FrameCounter.from(this.data.fadeOutCounter, 0.3);
    this.minAlpha = num(this.data.minAlpha, 0.2);
    this.spawnState = GemShardState.Spawned;
    this.localPosition = { x: this.go.local.tx, y: this.go.local.ty };
  }

  override start() {
    playOffset(this.go, 'StarCoinAnimation');
  }

  override onTriggerEnter(other: SimCollider) {
    if (this.isConsumed) return;
    const p = playerOf(other);
    if (p) this.onConsumed(p);
  }

  override fixedUpdate() {
    if (this.fadeOutCounter.isRunningNotFinished()) {
      this.setAlpha(this.fadeOutCounter.range(1, this.minAlpha));
      if (this.fadeOutCounter.countCheckFinishReset()) this.setAlpha(this.minAlpha);
    }
    const sc = this.spawnCounter;
    if (this.spawnState === GemShardState.Spawning && sc) {
      sc.count();
      if (sc.isFinished()) {
        this.setLocal(this.localPosition);
        if (!this.isConsumed) this.setAlpha(1);
        this.spawnState = GemShardState.Spawned;
      } else {
        this.setY(this.localPosition.y + sc.range(0, 0, FrameCounterRange.SineBounce) * this.bounceHeight);
        if (!this.isConsumed) this.setAlpha(sc.range(0, 1));
      }
    }
    if (this.spawnState === GemShardState.Despawning && sc) {
      sc.count();
      if (sc.isFinished()) {
        this.setLocal(this.localPosition);
        this.setAlpha(0);
        this.spawnState = GemShardState.Hidden;
      } else {
        this.setY(this.localPosition.y + sc.range(0, 0, FrameCounterRange.SineBounce, 1 - sc.percentage) * this.bounceHeight);
        this.setAlpha(sc.range(1, 0));
      }
    }
  }

  setAlpha(a: number) {
    setAlpha(this.spriteGo, a);
  }
  private setLocal(p: V2) {
    this.go.setLocal({ ...this.go.local, tx: p.x, ty: p.y });
  }
  private setY(y: number) {
    this.setLocal({ x: this.localPosition.x, y });
  }

  private onConsumed(p: PlayerController) {
    if (this.isConsumed) return;
    this.activate();
    this.isConsumed = true;
    this.fadeOutCounter.start();
    const c = this.go.colliders[0];
    if (c) c.enabled = false;
    this.sound('gemShard');
    this.game.levelAction('shard', p, this.go.position);
  }

  spawn(bounceHeight: number, spawnSpeed: number) {
    this.isConsumed = false;
    this.spawnCounter = FrameCounter.seconds(spawnSpeed);
    this.spawnCounter.start();
    this.spawnState = GemShardState.Spawning;
    this.bounceHeight = bounceHeight;
    const c = this.go.colliders[0];
    if (c) c.enabled = true;
  }

  despawn(bounceHeight: number, spawnSpeed: number, instant = false) {
    const c = this.go.colliders[0];
    if (c) c.enabled = false;
    if (instant) {
      this.spawnState = GemShardState.Hidden;
      this.isConsumed = false;
      this.setAlpha(0);
    } else {
      const pct = this.spawnCounter?.percentage ?? 1;
      this.spawnCounter = FrameCounter.seconds(spawnSpeed);
      this.spawnCounter.percentage = 1 - pct;
      this.spawnCounter.start();
      this.bounceHeight = bounceHeight;
      this.spawnState = GemShardState.Despawning;
    }
  }
}

const enum GroupPhase {
  Idle,
  Spawning,
  Active,
  Despawning,
  Done,
  SpawnStar,
}

export class GemShardGroup extends GameBehaviour {
  activator: ActivatorBase | null = null;
  canBeDeactivated = true;
  greenStar: Gem | null = null;
  lifetimeCounter = FrameCounter.seconds(8);
  xSweepSpeed = 0.2;
  sweepLeft = false;
  bounceHeight = 1;
  spawnSpeed = 0.6;
  circleExpandSpeed = 0.15;
  coinMoveMaxSpeed = 2;
  coinMoveAccel = 0.01;
  starSpawnCircleRad = 0;
  coinCountAtStar = 0;
  phase = GroupPhase.Idle;
  coins: GemShard[] = [];
  xSweepPos = 0;
  override noSave = ['activator', 'greenStar', 'coins'];

  override awake() {
    const d = this.data;
    this.canBeDeactivated = bool(d.canBeDeactivated, true);
    this.lifetimeCounter = FrameCounter.from(d.lifetimeCounter, 8);
    this.xSweepSpeed = num(d.xSweepSpeed, 0.2);
    this.sweepLeft = num(d.xSweepDirection, 1) === 0;
    this.bounceHeight = num(d.bounceHeight, 1);
    this.spawnSpeed = num(d.spawnSpeed, 0.6);
    this.circleExpandSpeed = num(d.circleExpandSpeed, 0.15);
    this.coinMoveMaxSpeed = num(d.coinMoveMaxSpeed, 2);
    this.coinMoveAccel = num(d.coinMoveAccel, 0.01);
  }

  override start() {
    this.greenStar = this.go.getBehaviourInChildren(Gem);
    this.greenStar?.despawn();
    this.coins = this.go.getBehavioursInChildren(GemShard, true);
    this.activator = this.refBehaviour(this.data.activator, ActivatorBase as any) as ActivatorBase | null;
    if (this.activator) {
      this.activator.listeners.push((a) => this.onActivatorChanged(a));
      this.phase = GroupPhase.Idle;
      for (const c of this.coins) c.despawn(this.bounceHeight, this.spawnSpeed, true);
    } else this.phase = GroupPhase.Active;
  }

  private onActivatorChanged(active: boolean) {
    if (active && this.phase === GroupPhase.Idle) this.startPhase(GroupPhase.Spawning);
    else if (this.canBeDeactivated) this.startPhase(GroupPhase.Despawning);
  }

  private startPhase(p: GroupPhase) {
    const xs = this.coins.map((c) => c.go.position.x);
    this.xSweepPos = this.sweepLeft ? Math.max(...xs) : Math.min(...xs);
    this.phase = p;
  }

  override fixedUpdate() {
    switch (this.phase) {
      case GroupPhase.Spawning: this.tickSweep(true); break;
      case GroupPhase.Active: this.tickActive(); break;
      case GroupPhase.Despawning: this.tickSweep(false); break;
      case GroupPhase.SpawnStar: this.tickSpawnStar(); break;
    }
  }

  private tickSweep(spawning: boolean) {
    this.xSweepPos += this.xSweepSpeed * (this.sweepLeft ? -1 : 1);
    let all = true;
    for (const c of this.coins) {
      const x = c.go.position.x;
      if ((!this.sweepLeft && x < this.xSweepPos) || (this.sweepLeft && x > this.xSweepPos)) {
        if (spawning && (c.spawnState === GemShardState.Hidden || c.spawnState === GemShardState.Despawning)) c.spawn(this.bounceHeight, this.spawnSpeed);
        if (!spawning && (c.spawnState === GemShardState.Spawned || c.spawnState === GemShardState.Spawning)) c.despawn(this.bounceHeight, this.spawnSpeed);
      } else all = false;
    }
    if (!all) return;
    if (spawning) {
      this.lifetimeCounter.start();
      this.phase = GroupPhase.Active;
    } else this.phase = GroupPhase.Done;
  }

  private tickSpawnStar() {
    const star = this.greenStar;
    if (!star) return;
    this.starSpawnCircleRad += this.circleExpandSpeed;
    const sp = star.go.position;
    for (const c of this.coins) {
      const p = c.go.position;
      if (Math.hypot(p.x - sp.x, p.y - sp.y) < this.starSpawnCircleRad && c.spawnState === GemShardState.Spawned) {
        c.setAlpha(1);
        c.moveTowardsStarSpeed = Math.min(c.moveTowardsStarSpeed + this.coinMoveAccel, this.coinMoveMaxSpeed);
        const np = moveTowardsV(p, sp, c.moveTowardsStarSpeed);
        c.go.position = np;
        if (np.x === sp.x && np.y === sp.y) {
          c.despawn(this.bounceHeight, this.spawnSpeed, true);
          this.coinCountAtStar++;
        }
      }
    }
    if (this.coinCountAtStar > 0) star.setAlpha(this.coinCountAtStar / this.coins.length);
    if (this.coinCountAtStar === this.coins.length) {
      this.phase = GroupPhase.Done;
      const c = star.go.colliders[0];
      if (c) c.enabled = true;
    }
  }

  private tickActive() {
    if (this.activator && this.lifetimeCounter.countCheckFinishReset()) {
      this.startPhase(GroupPhase.Despawning);
      return;
    }
    const star = this.greenStar;
    if (star && !star.isSpawned && this.coins.every((c) => c.isConsumed)) {
      star.isSpawned = true;
      this.phase = GroupPhase.SpawnStar;
      this.sound('shardsComplete', star.go.position);
      if (star.go.sprite) star.go.sprite.enabled = true;
      star.setAlpha(0);
    }
  }
}

export class PowerupCollectable extends ActivatorBase {
  powerupType = 0 as PowerupType;
  isConsumed = false;
  spriteGo: SimGo | null = null;
  override noSave = ['spriteGo'];
  override awake() {
    this.powerupType = num(this.data.powerupType) as PowerupType;
  }
  override start() {
    this.spriteGo = spriteGoIn(this.go);
    this.updateSprite();
  }
  override fixedUpdate() {
    const col = this.go.colliders[0];
    if (!col) return;
    for (const h of this.world.overlapCollider(col)) {
      const p = playerOf(h);
      if (p) this.checkAndConsume(p);
    }
  }
  checkAndConsume(p: PlayerController) {
    if (!this.isConsumed && (p.powerup?.type ?? 0) !== this.powerupType) {
      p.givePowerup(this.powerupType);
      this.isConsumed = true;
      this.setActiveState(true);
      this.updateSprite();
      this.game.levelAction('powerup', p, this.go.position);
    }
  }
  private updateSprite() {
    if (this.isConsumed && this.spriteGo?.sprite) this.spriteGo.sprite.enabled = false;
  }
}

export class ReDash extends ActivatorBase {
  availableSprite: SpriteRef | null = null;
  consumedSprite: SpriteRef | null = null;
  respawnTime = FrameCounter.seconds(1);
  isConsumed = false;
  spriteGo: SimGo | null = null;
  touchingPlayers = new Set<PlayerController>();
  override noSave = ['availableSprite', 'consumedSprite', 'spriteGo'];
  override awake() {
    this.availableSprite = spriteRef(this.game, this.data.availableSprite);
    this.consumedSprite = spriteRef(this.game, this.data.consumedSprite);
    this.respawnTime = FrameCounter.from(this.data.respawnTime, 1);
  }
  override start() {
    this.spriteGo = spriteGoIn(this.go);
    this.updateSprite();
  }
  private checkAndConsume(p: PlayerController) {
    if (!this.isConsumed && p.dashCountSinceLastGround > 0) {
      p.dashCountSinceLastGround = 0;
      this.consumedChange(true);
      this.sound('reDash');
    }
  }
  override fixedUpdate() {
    if (this.isConsumed && this.respawnTime.countCheckFinishReset()) this.consumedChange(false);
    for (const p of this.touchingPlayers) this.checkAndConsume(p);
  }
  private consumedChange(c: boolean) {
    this.isConsumed = c;
    this.setActiveState(c);
    this.updateSprite();
    if (c) this.respawnTime.start();
    else this.respawnTime.reset();
  }
  private updateSprite() {
    const s = this.isConsumed ? this.consumedSprite : this.availableSprite;
    if (this.spriteGo?.sprite && s) this.spriteGo.sprite.sprite = s;
  }
  override onTriggerEnter(other: SimCollider) {
    const p = playerOf(other);
    if (p) this.touchingPlayers.add(p);
  }
  override onTriggerExit(other: SimCollider) {
    const p = playerOf(other);
    if (p) this.touchingPlayers.delete(p);
  }
}

export class RePack extends GameBehaviour {
  respawnTime = FrameCounter.seconds(3);
  fadeInTime = FrameCounter.seconds(0.5);
  jetpackRefillPercentage = 1;
  isConsumed = false;
  spriteGo: SimGo | null = null;
  override noSave = ['spriteGo'];
  override awake() {
    this.respawnTime = FrameCounter.from(this.data.respawnTime, 3);
    this.fadeInTime = FrameCounter.from(this.data.fadeInTime, 0.5);
    this.jetpackRefillPercentage = num(this.data.jetpackRefillPercentage, 1);
  }
  override start() {
    this.spriteGo = spriteGoIn(this.go);
  }
  override fixedUpdate() {
    let just = false;
    const col = this.go.colliders[0];
    if (!this.isConsumed && col) {
      for (const h of this.world.overlapCollider(col)) {
        const p = playerOf(h);
        if (p?.powerup instanceof Jetpack) {
          p.powerup.refill(this.jetpackRefillPercentage);
          just = true;
        }
      }
    }
    if (just) {
      this.sound('rePack');
      this.isConsumed = true;
      setAlpha(this.spriteGo, 0);
      this.respawnTime.start();
      this.fadeInTime.reset();
    }
    if (this.respawnTime.countCheckFinishReset()) {
      this.isConsumed = false;
      this.fadeInTime.start();
    }
    if (this.fadeInTime.isRunningNotFinished()) {
      this.fadeInTime.count();
      setAlpha(this.spriteGo, this.fadeInTime.percentage);
    }
  }
}

export class ForceDash extends ActivatorBase {
  respawnTime = FrameCounter.seconds(1);
  fadeInTime = FrameCounter.seconds(0.5);
  color1: RGBA = [0, 1, 0, 1];
  color2: RGBA = [0, 0, 1, 1];
  colorFadeCounter = FrameCounter.seconds(1);
  isConsumed = false;
  touchingPlayer: PlayerController | null = null;
  lastPlayerDist = 100;
  lastPlayerVectorDist = 100;
  override awake() {
    const d = this.data;
    this.respawnTime = FrameCounter.from(d.respawnTime, 1);
    this.fadeInTime = FrameCounter.from(d.fadeInTime, 0.5);
    this.color1 = rgba(d.color1, [0, 1, 0, 1]);
    this.color2 = rgba(d.color2, [0, 0, 1, 1]);
    this.colorFadeCounter = FrameCounter.from(d.colorFadeCounter, 1);
  }
  override start() {
    this.colorFadeCounter.start();
  }
  private right(): V2 {
    const m = this.go.worldXform();
    const l = Math.hypot(m.a, m.b) || 1;
    return { x: m.a / l, y: m.b / l };
  }
  private checkAndConsume() {
    if (this.isConsumed || !this.touchingPlayer) return;
    this.touchingPlayer.body.movePosition(this.go.position);
    this.touchingPlayer.startDash(this.right(), true);
    this.consumedChange(true);
    this.sound('forceDash');
  }
  override fixedUpdate() {
    if (this.isConsumed && this.respawnTime.countCheckFinishReset()) this.consumedChange(false);
    if (this.fadeInTime.isRunningNotFinished()) {
      if (this.fadeInTime.countCheckFinishReset()) setAlpha(this.go, 1);
      else setAlpha(this.go, this.fadeInTime.percentage);
    }
    if (this.colorFadeCounter.countCheckFinishReset()) this.colorFadeCounter.start();
    const t = this.colorFadeCounter.range(0, 1, FrameCounterRange.SineBounce);
    const sr = this.go.sprite;
    if (sr) {
      const c1 = this.color1, c2 = this.color2;
      // Color.Lerp clamps t to [0, 1]
      const k = Math.max(0, Math.min(1, t));
      sr.color = [lerp(c1[0], c2[0], k), lerp(c1[1], c2[1], k), lerp(c1[2], c2[2], k), sr.color[3]];
    }
    const col = this.go.colliders[0];
    let clear = true;
    if (col) {
      for (const h of this.world.overlapCollider(col)) {
        const p = playerOf(h);
        if (!p) continue;
        if (this.touchingPlayer === p) clear = false;
        if (!this.touchingPlayer) {
          clear = false;
          this.touchingPlayer = p;
          this.lastPlayerDist = 100;
          this.lastPlayerVectorDist = 100;
        }
      }
    }
    if (clear) this.touchingPlayer = null;
    const tp = this.touchingPlayer;
    if (tp) {
      const a = this.go.position, p = tp.go.position, r = this.right();
      const dist = Math.hypot(p.x - a.x, p.y - a.y);
      const vdist = Math.abs((p.x - a.x) * r.y - (p.y - a.y) * r.x);
      if (dist > this.lastPlayerDist || vdist > this.lastPlayerVectorDist) this.checkAndConsume();
      this.lastPlayerDist = dist;
      this.lastPlayerVectorDist = vdist;
    }
  }
  private consumedChange(c: boolean) {
    this.isConsumed = c;
    this.setActiveState(c);
    if (c) {
      this.respawnTime.restart();
      this.fadeInTime.reset();
      setAlpha(this.go, 0);
    } else {
      this.respawnTime.reset();
      this.fadeInTime.restart();
    }
  }
}